import type { TreatmentPlanDto } from '../api/generated';

export type RowMoveDirection = 'up' | 'down';

// A Gantt row is every plan sharing a template, ordered by where the template
// first appears in the plan array (the rule buildGanttGroups groups by). So the
// row order is the plan array order, and moving a row is a reorder of that array
// — which is what gets persisted, since the save replaces the list wholesale.
function collectTemplateIdsInOrder(plans: TreatmentPlanDto[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const plan of plans) {
    if (seen.has(plan.treatmentPlanTemplateId)) continue;
    seen.add(plan.treatmentPlanTemplateId);
    ordered.push(plan.treatmentPlanTemplateId);
  }
  return ordered;
}

// Returns the input array itself when the move cannot happen (unknown template,
// or already at the edge in that direction), so callers can tell "nothing moved"
// from a real reorder by reference and leave the plan clean.
//
// Rebuilding group by group canonicalises interleaved plans (A1,B1,A2 →
// A1,A2,B1). That is a change the array records but the chart does not show,
// because the chart already draws the groups this way.
export function moveTreatmentPlanRow(
  plans: TreatmentPlanDto[],
  templateId: string,
  direction: RowMoveDirection
): TreatmentPlanDto[] {
  const templateIds = collectTemplateIdsInOrder(plans);
  const index = templateIds.indexOf(templateId);
  if (index === -1) return plans;

  const targetIndex = direction === 'up' ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= templateIds.length) return plans;

  const reordered = [...templateIds];
  reordered[index] = templateIds[targetIndex];
  reordered[targetIndex] = templateId;

  return reordered.flatMap((id) => plans.filter((plan) => plan.treatmentPlanTemplateId === id));
}
